"use client";

import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Clock, MapPin, PhoneCall, MessageCircle, CircleCheck as CheckCircle2, Flame, Zap, Wrench, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { PHONE_E164, PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
export default function OvenCleaningServiceDetails() {
  const whyChooseUs = [{
    title: "خبرة متخصصة ومعتمدة",
    description: "سنوات من الممارسة الفعلية في تنظيف وصيانة مختلف أنواع وموديلات الأفران المنزلية.",
    icon: ShieldCheck
  }, {
    title: "كوادر فنية مدربة",
    description: "فريق فني محترف يتعامل بدقة مع أجزاء الفرن الحساسة، والشعلات، والمفاتيح الكهربائية.",
    icon: Wrench
  }, {
    title: "منظفات آمنة ومصرحة",
    description: "مواد إذابة دهون قوية وفعالة خالية من الروائح الكيميائية الضارة وآمنة تماماً على الأسطح والصحة.",
    icon: Sparkles
  }, {
    title: "خدمة منزلية فورية بالرياض",
    description: "وصول سريع ومواعيد دقيقة لكافة أحياء الرياض مع توفير كامل المعدات في زيارة واحدة.",
    icon: Clock
  }];
  const serviceFeatures = ["إذابة الدهون المستعصية والزيوت المتراكمة على الجدران الداخلية", "تلميع الزجاج الداخلي والخارجي للباب وإزالة التصبغات والضبابية", "تنظيف وغسيل الشوايات، الصواني، والشبكات المعدنية بالكامل", "تنظيف العيون والشعلات ومسارات تدفق الغاز لتحسين كفاءة الاشتعال", "إزالة الروائح الكريهة وتعقيم الفرن بمواد متخصصة عالية الأمان", "فحص سلامة العوازل المطاطية وإحكام غلق باب الفرن"];
  const faqs = [{
    id: "faq-1",
    question: "كم تستغرق عملية تنظيف وغسيل الفرن بالكامل؟",
    answer: "تستغرق عملية التنظيف الشاملة عادة ما بين 60 إلى 90 دقيقة بحسب حجم الفرن ودرجة تراكم الدهون، حيث تشمل فك الأجزاء القابلة للغسيل، تنظيف الحجرة الداخلية، والتعقيم الكامل."
  }, {
    id: "faq-2",
    question: "هل الخدمة ملائمة للأفران الغازية والأفران الكهربائية؟",
    answer: "نعم، نقدم الخدمة للأفران الغازية والكهربائية على حد سواء، إضافة إلى الأفران المدمجة (Built-in)، مع استخدام أساليب مخصصة تحمي الدوائر الكهربائية وحساسات الأمان."
  }, {
    id: "faq-3",
    question: "ما هو المعدل الزمني الموصى به لتنظيف الأفران دورياً؟",
    answer: "يُفضل إجراء تنظيف عميق متخصص كل 3 إلى 6 أشهر للمحافظة على كفاءة التسخين، منع تصاعد الأدخنة والروائح، وضمان نظافة الأطعمة وسلامة بيئة المطبخ."
  }, {
    id: "faq-4",
    question: "كيف يمكنني حجز موعد لخدمة تنظيف الفرن بالمنزل؟",
    answer: `يمكنك حجز موعدك فوراً وبسهولة من خلال التواصل المباشر هاتفياً أو عبر الواتساب على الرقم المعتمد ${PHONE_E164}، وتحديد الوقت المناسب لزيارة الفني لموقعك في الرياض.`
  }];
  return <section id="ovencleaningservicedetails" data-nav="light" dir="rtl" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl space-y-20">
        
        {/* Block 1: Service Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-5">
            <div className="inline-flex items-center gap-2">
              <span className="h-1 w-8 bg-primary rounded-full" />
              <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5 px-3 py-1 font-medium">
                خدمة غسيل متخصصة
              </Badge>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground leading-tight">
              تنظيف وغسيل الأفران بالرياض بأعلى معايير الدقة والسلامة
            </h2>
            
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              نقدم في لمسة احتراف خدمة تنظيف وغسيل شاملة للأفران المنزلية؛ تشمل إزالة الدهون الصعبة، الزيوت المتكلسة، وغسيل الشوايات والصواني وأبواب الزجاج، مع التخلص التام من الروائح غير المرغوبة باستخدام مواد تنظيف احترافية وآمنة على كافة أنواع الأفران.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {serviceFeatures.map((feat, index) => <div key={index} data-index={index} className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground/90 leading-snug">{feat}</span>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-md bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_cleaning_riyadh_service.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_cleaning_riyadh_service.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_cleaning_riyadh_service.webp-wvc-sizes" alt="تنظيف وغسيل الأفران بالرياض لمسة احتراف" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover object-center" />
              <div className="p-4 bg-muted/60 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5 font-medium text-foreground">
                  <Flame className="size-4 text-primary" /> أفران الغاز والكهرباء
                </span>
                <span>خدمة منزلية داخل الرياض</span>
              </div>
            </div>
          </div>
        </div>

        {/* Block 2: Why Choose Us */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              لماذا تختار لمسة احتراف لتنظيف فرنك؟
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              نلتزم بتقديم أعلى مستويات العناية والنظافة لضمان عمل الفرن بكفاءة تامة وتوفير بيئة طهي صحية.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={i} data-index={i} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: i * 0.08
            }}>
                  <Card className="h-full border-border/80 hover:border-primary/40 transition-all hover:shadow-md bg-card">
                    <CardHeader className="space-y-3 pb-3">
                      <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center ring-1 ring-primary/20">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Block 3: Service Coverage Band */}
        <div className="rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 relative overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8 space-y-3">
              <div className="flex items-center gap-2 text-primary font-medium text-sm">
                <MapPin className="size-4" />
                <span>تغطية جغرافية كاملة</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                تغطية شاملة لكافة أحياء ومناطق الرياض
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                تصلك فرقنا المتنقلة والمجهزة بكافة معدات ومواد التنظيف المتطورة مباشرة إلى باب منزلك في شمال، شرق، غرب، وجنوب الرياض، بما يشمل أحياء الياسمين، الملقا، العقيق، النخيل، وكافة الأحياء المجاورة.
              </p>
            </div>
            <div className="md:col-span-4 flex justify-start md:justify-end">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to={WHATSAPP_HREF} newTab>
                  <MessageCircle className="size-4 ml-2" />
                  اطلب الخدمة لمنزلك
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Block 4: FAQ Section */}
        <div className="space-y-8 max-w-3xl mx-auto">
          <div className="text-center space-y-2">
            <Badge variant="outline" className="text-primary border-primary/20">الأسئلة الشائعة</Badge>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              كل ما تود معرفته عن خدمة تنظيف الأفران
            </h3>
          </div>

          <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-3">
            {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border border-border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-base font-semibold text-foreground py-4 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pt-1 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Block 5: Direct Conversion CTA */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 text-center shadow-lg relative overflow-hidden">
          <div className="max-w-2xl mx-auto space-y-6 relative z-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-secondary-foreground">
              جاهزون لإعادة فرنك نظيفاً ولامعاً كالجديد؟
            </h3>
            <p className="text-secondary-foreground/80 text-sm sm:text-base leading-relaxed">
              احجز موعد الزيارة المنزلية الآن في أي حي بالرياض. خدمة موثوقة وسريعة مع شركة لمسة احتراف.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20bd5a] text-white border-0 shadow-md font-semibold text-base px-6 h-11">
                <Link to={WHATSAPP_HREF} newTab>
                  <MessageCircle className="size-5 ml-2" />
                  حجز عبر الواتساب
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent font-medium h-11">
                <a href={PHONE_TEL_HREF}>
                  <PhoneCall className="size-4 ml-2" />
                  اتصال مباشر: {PHONE_LOCAL}
                </a>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}